import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

interface BrandMarkProps {
  size?: number;
}

/**
 * AgentX glyph: the stylised "A" from the AgentX wordmark (traced from the brand
 * artwork), white on a solid black tile — same mark as the favicon. In dark mode
 * the tile is inverted (light tile, black A) so it doesn't vanish into the
 * near-black surfaces. Kept as SVG so it stays crisp at any size.
 */
export const BrandMark: React.FC<BrandMarkProps> = ({ size = 34 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tile = isDark ? '#f5f5f5' : '#111111';
  const glyph = isDark ? '#111111' : '#ffffff';

  return (
    <Box
      component="svg"
      viewBox="-59.72 -110.73 587.73 587.73"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      sx={{ display: 'block', flexShrink: 0 }}
    >
      <rect x="-59.72" y="-110.73" width="587.73" height="587.73" rx="105.8" fill={tile} />
      <path fillRule="evenodd" fill={glyph} d="M194.35 357.63C198.89 355.73 271.0 287.28 271.0 284.86C271.0 283.5 103.53 283.76 98.96 285.13C95.97 286.03 87.91 293.55 59.85 321.64C40.41 341.1 24.35 357.46 24.17 358.01C23.95 358.66 52.64 359.0 107.45 359.0C176.02 359.0 191.65 358.75 194.35 357.63ZM450.94 354.17C452.81 353.17 455.17 350.81 456.17 348.94C457.88 345.76 458.0 341.96 458.0 293.02C458.0 264.13 458.48 201.18 459.06 153.11C460.2 58.55 460.14 56.9 455.05 44.79C448.09 28.23 432.65 15.51 412.85 10.03C403.19 7.35 386.44 7.27 376.49 9.86C367.74 12.13 357.65 17.1 350.77 22.51C346.17 26.13 317.02 55.82 280.05 94.52C273.76 101.11 251.24 124.5 230.01 146.5C208.78 168.5 180.03 198.31 166.11 212.75L140.82 239.0L89.66 239.01C56.39 239.01 37.15 239.39 34.64 240.08C31.94 240.83 27.38 244.29 19.35 251.7C13.06 257.5 8.1 262.43 8.32 262.65C8.54 262.87 92.73 263.04 195.41 263.02C357.71 262.98 382.94 263.17 388.55 264.47C408.52 269.11 424.45 285.27 427.92 304.43C428.52 307.7 429.0 320.63 429.0 333.18L429.0 356.0L438.27 356.0C445.33 356.0 448.34 355.57 450.94 354.17ZM273.0 237.18C273.0 236.73 280.54 228.24 289.75 218.32C298.96 208.39 315.33 190.65 326.12 178.88C336.92 167.12 352.36 150.3 360.44 141.5C376.96 123.5 379.99 120.9 387.91 117.91C392.34 116.24 395.06 115.89 401.0 116.23C414.03 116.98 423.47 122.94 428.17 133.39C430.31 138.15 430.5 140.08 430.82 161.22C431.24 189.13 430.39 195.62 424.83 207.0C417.68 221.63 404.58 232.49 389.36 236.42C384.11 237.77 375.14 238.0 328.11 238.0C297.8 238.0 273.0 237.63 273.0 237.18Z" />
    </Box>
  );
};

interface BrandProps {
  /** Hide the wordmark — used by the collapsed sidebar rail. */
  compact?: boolean;
  size?: number;
  subtitle?: string;
}

export const Brand: React.FC<BrandProps> = ({ compact = false, size = 34, subtitle = 'Manager' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
    <BrandMark size={size} />
    {!compact && (
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          sx={{ display: 'block', fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}
        >
          AgentX
        </Typography>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            lineHeight: 1.2,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    )}
  </Box>
);

export default Brand;
